import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';

type RevenuePoint = { bucket: string; amount: number; count: number };
type RevenueResponse = {
  mode?: 'test' | 'live';
  currency: string;
  interval: 'day' | 'month' | 'year';
  date_from: string;
  date_to: string;
  total_amount: number;
  total_count: number;
  series: RevenuePoint[];
};

type KpisResponse = {
  mode: 'test' | 'live';
  db: { total: number; active: number; basic_active: number; full_active: number };
  b2b?: { total: number; active: number };
  stripe: { active: number; cancel_at_period_end: number; mrr_amount: number; currency: string };
};

function formatMoneyCents(amount: number, currency: string) {
  const cur = (currency || '').toUpperCase();
  if (!Number.isFinite(amount)) return '—';
  const v = amount / 100;
  try {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: cur === 'N/A' ? 'EUR' : cur }).format(v);
  } catch {
    return `${v.toFixed(2)} ${cur || ''}`.trim();
  }
}

function isoDate(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function PaymentsOverviewPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<KpisResponse | null>(null);
  const [revenue7d, setRevenue7d] = useState<RevenueResponse | null>(null);

  const dateFrom = useMemo(() => isoDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), []);
  const dateTo = useMemo(() => isoDate(new Date()), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kpisResp, revResp] = await Promise.all([
        api.get('/api/billing/platform/kpis'),
        api.get('/api/billing/platform/revenue', { params: { interval: 'day', date_from: dateFrom, date_to: dateTo } }),
      ]);
      setKpis(kpisResp.data as any);
      setRevenue7d(revResp.data as any);
    } catch (e: any) {
      // Keep the overview usable even if Stripe isn't configured.
      setKpis(null);
      setRevenue7d(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const mode = (revenue7d?.mode || kpis?.mode || 'test').toUpperCase();

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-foreground">Plăți & abonamente</div>
          <div className="text-sm text-muted-foreground">Sumar rapid (7 zile) + status Stripe.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" iconName="RefreshCw" onClick={() => void load()} disabled={loading}>
            Reîncarcă
          </Button>
          <Button variant="default" iconName="BarChart3" onClick={() => navigate('/platform-admin-dashboard?tab=reports')}>
            Deschide rapoarte
          </Button>
        </div>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4">
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">MRR (Stripe)</div>
          <div className="text-lg font-semibold text-foreground">
            {formatMoneyCents(kpis?.stripe?.mrr_amount || 0, kpis?.stripe?.currency || 'N/A')}
          </div>
          <div className="text-xs text-muted-foreground">Mod: {mode}</div>
        </div>

        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Încasări 7 zile</div>
          <div className="text-lg font-semibold text-foreground">
            {formatMoneyCents(revenue7d?.total_amount || 0, revenue7d?.currency || 'N/A')}
          </div>
          <div className="text-xs text-muted-foreground">Tranzacții: {revenue7d?.total_count ?? 0}</div>
        </div>

        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Abonamente active</div>
          <div className="text-lg font-semibold text-foreground">{kpis?.stripe?.active ?? 0}</div>
          <div className="text-xs text-muted-foreground">La final de perioadă: {kpis?.stripe?.cancel_at_period_end ?? 0}</div>
        </div>

        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">DB (abonamente)</div>
          <div className="text-lg font-semibold text-foreground">{kpis?.db?.active ?? 0} active</div>
          <div className="text-xs text-muted-foreground">
            Basic: {kpis?.db?.basic_active ?? 0} • Full: {kpis?.db?.full_active ?? 0}
          </div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Școli în B2B</div>
          <div className="text-lg font-semibold text-foreground">{kpis?.b2b?.active ?? 0}</div>
          <div className="text-xs text-muted-foreground">Total: {kpis?.b2b?.total ?? 0}</div>
        </div>
      </div>

      {!kpis && !revenue7d ? (
        <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
          <div>
            Nu am putut încărca datele Stripe (posibil neconfigurat). Rapoartele rămân disponibile după setarea cheilor Stripe.
          </div>
        </div>
      ) : null}
    </div>
  );
}
